import {
    Bell,
    ChartSpline,
    Database,
    Inbox,
    LayoutDashboard,
    LibraryBig,
    NotebookText,
    Play,
    Plug,
    Tag,
    TriangleAlert,
    University,
    UserRound,
    Users,
    type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Avatar,
    AvatarGradientFallback,
    Text,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    Heading,
    Badge,
    useSidebar,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { useQuery } from "@apollo/client/react";
import type { Role } from "@athena/common/graphql/generated/schema";
import { CurrentUserDocument } from "../graphql/auth.generated";
import { ShellSwitch } from "./ShellSwitch";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { deployedEnvironment } from "@/lib/env";

/**
 * Which roles may see each nav item.
 *
 * This is presentation only — hiding a tab is not authorization. Every page behind these
 * links is gated server-side (see the `@auth` directives and `requireOrgAdmin`/`requireStaff`
 * checks in services/graphql), and typing the URL by hand still hits those. The point here is
 * that an org admin shouldn't be shown five tabs that would only greet them with an error.
 *
 * ORG_ADMIN and SUB_ORG_ADMIN see the same tabs on purpose: they differ in *reach* (one
 * covers its sub-orgs, the other only itself — see schema/users.ts), and that scoping is
 * applied to the data each page loads, not to which pages exist.
 */
const STAFF: Role[] = [
    "ADMIN",
    "SYSTEM",
];
const ORG_ADMINS: Role[] = [
    "ORG_ADMIN",
    "SUB_ORG_ADMIN",
];

const engagementNavItems = [
    {
        label: "Coaching Chat",
        icon: Inbox,
        path: "/inbox",
        activePaths: ["/inbox"],
        badge: null, //TODO: make badge count dynamic
        roles: [
            ...STAFF,
            "COACH" as Role,
        ],
    },
    {
        label: "Push Notifications",
        icon: Bell,
        path: "/push-notifications",
        activePaths: ["/push-notifications"],
        badge: null,
        /*
         * Not COACH. Every operation this page needs refuses one: `sendPushNotification` and
         * `pushNotificationHistory` are ADMIN/SYSTEM/ORG_ADMIN/SUB_ORG_ADMIN, and the recipient
         * picker reads `users`, which is the same set. A coach reached a page where the list
         * would not load and the send button returned 403 — the API was right, the nav was not.
         *
         * ORG_ADMIN and SUB_ORG_ADMIN are *permitted* by all three of those operations and still
         * absent here. That gap is older than this change and giving partners a send button is a
         * product decision, not a correction, so it is left alone and noted rather than taken.
         */
        roles: [...STAFF],
    },
    {
        label: "Crisis Management",
        icon: TriangleAlert,
        path: "/crisis-management",
        activePaths: ["/crisis-management"],
        badge: null,
        /*
         * Not COACH either, and for a sharper reason: `crisisResources` is readable by any signed
         * in caller, so the page would *load* — and then every button on it fails, because
         * creating, updating and deleting keywords and resources are all ADMIN/SYSTEM. A
         * read-only view of an editor is worse than no entry at all.
         */
        roles: [...STAFF],
    },
];

const workspaceNavItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/", activePaths: ["/"], badge: null, roles: STAFF },
    {
        label: "Organizations",
        icon: University,
        path: "/organizations",
        activePaths: ["/organizations"],
        badge: null,
        roles: STAFF,
    },
    {
        label: "Users",
        icon: Users,
        path: "/users",
        activePaths: ["/users"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
            "COACH" as Role,
        ],
    },
    {
        label: "Integrations",
        icon: Plug,
        path: "/integrations",
        activePaths: ["/integrations"],
        badge: null,
        roles: STAFF,
    },
    {
        label: "Data",
        icon: Database,
        path: "/data",
        activePaths: ["/data"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
        ],
    },
    { label: "Stats Report", icon: ChartSpline, path: "/stats", activePaths: ["/stats"], badge: null, roles: STAFF },
];

const contentNavItems = [
    {
        // The page it opens is the course list and calls itself Courses. "Content" also named the
        // group heading above it, so the sidebar read "CONTENT › Content".
        label: "Courses",
        icon: Play,
        path: "/courses",
        activePaths: [
            "/courses",
            "/course",
        ],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
            "COACH" as Role,
        ],
    },
    {
        label: "Video Library",
        icon: LibraryBig,
        path: "/library",
        activePaths: ["/library"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
            "COACH" as Role,
        ],
    },
    {
        label: "Presenters",
        icon: UserRound,
        path: "/presenters",
        activePaths: ["/presenters"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
            "COACH" as Role,
        ],
    },
    {
        label: "Tags",
        icon: Tag,
        path: "/tags",
        activePaths: ["/tags"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
            "COACH" as Role,
        ],
    },
    {
        label: "Course Assignments",
        icon: NotebookText,
        path: "/assignments",
        activePaths: ["/assignments"],
        badge: null,
        roles: [
            ...STAFF,
            ...ORG_ADMINS,
        ],
    },
];

interface NavItem {
    label: string;
    icon: LucideIcon;
    path: string;
    activePaths: string[];
    badge: string | null;
    roles: Role[];
}

/**
 * One labelled group of nav links, already filtered to the caller's role. Renders nothing
 * when the role can see none of its items, so a role never gets a bare group heading with
 * nothing under it.
 */
function NavGroup({ label, items, onNavigate }: { label: string; items: NavItem[]; onNavigate: (p: string) => void }) {
    const location = useLocation();
    if (items.length === 0) return null;

    return (
        <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-muted-foreground font-mono text-xs font-semibold tracking-widest uppercase">
                {label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
                <SidebarMenu>
                    {items.map((item) => {
                        const isActive = item.activePaths.some((p) =>
                            p === "/" ? location.pathname === "/" : location.pathname.startsWith(p),
                        );
                        return (
                            <SidebarMenuItem key={item.label}>
                                <SidebarMenuButton
                                    onClick={() => onNavigate(item.path)}
                                    className={cn(
                                        "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground relative w-full",
                                        isActive &&
                                            "before:bg-ring text-sidebar-foreground font-semibold before:absolute before:top-1 before:bottom-1 before:left-0 before:w-[3px] before:rounded-full",
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                                            isActive && "bg-ring/15",
                                        )}
                                    >
                                        <item.icon className={cn("h-4 w-4", isActive && "text-ring")} />
                                    </span>
                                    <span>{item.label}</span>
                                </SidebarMenuButton>
                                {item.badge && (
                                    <SidebarMenuBadge className="bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground rounded-full px-1.5 text-xs">
                                        {item.badge}
                                    </SidebarMenuBadge>
                                )}
                            </SidebarMenuItem>
                        );
                    })}
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
}

export function AppSidebar() {
    const environmentBadge = deployedEnvironment();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data, loading } = useQuery(CurrentUserDocument);
    const user = data?.currentUser;
    const { isMobile, setOpenMobile } = useSidebar();

    const handleNavigate = (path: string) => {
        navigate(path);
        if (isMobile) setOpenMobile(false);
    };

    // No role yet (still loading, or signed out) shows no links rather than every link —
    // erring toward hiding, so tabs can't flash before the role is known.
    const role = user?.role;
    const visible = (items: NavItem[]) => (role ? items.filter((item) => item.roles.includes(role)) : []);

    return (
        <Sidebar collapsible="icon" className="border-border z-30 border-r" variant="sidebar">
            <SidebarHeader className="p-4">
                <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
                    <Avatar square size="md" className="shrink-0">
                        <AvatarGradientFallback>RU</AvatarGradientFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                        <div className="flex items-center gap-2">
                            <Heading level={5} className="text-sidebar-foreground">
                                {t("common.appName")}
                            </Heading>
                            {environmentBadge ? (
                                <Badge
                                    variant="warning"
                                    className="shrink-0 px-1.5 py-0 font-mono text-[10px] uppercase"
                                >
                                    {environmentBadge}
                                </Badge>
                            ) : null}
                        </div>
                        <Text className="text-sidebar-muted-foreground text-xs">Admin Portal</Text>
                    </div>
                </div>
            </SidebarHeader>
            <SidebarContent className="px-2">
                <NavGroup label={t("nav.workspace")} items={visible(workspaceNavItems)} onNavigate={handleNavigate} />
                <NavGroup label={t("nav.content")} items={visible(contentNavItems)} onNavigate={handleNavigate} />
                <NavGroup label={t("nav.engagement")} items={visible(engagementNavItems)} onNavigate={handleNavigate} />
            </SidebarContent>

            <SidebarFooter className="p-4">
                <ShellSwitch />
                <SidebarUserMenu user={user} loading={loading} />
            </SidebarFooter>
        </Sidebar>
    );
}
